<script setup lang="ts">
  import {
    computed,
    ref,
    provide,
    type ComponentPublicInstance,
    nextTick,
    watch,
    onBeforeUnmount,
  } from 'vue';
  import { storeToRefs } from 'pinia';
  import {
    removeFormBuilderBodyTable,
    removeFormBuilderCanvasElement,
    removeFormBuilderTableCellElement,
  } from '../../composables/removeFormBuilderCanvasElement';
  import { useModal } from 'vue-final-modal';
  import { useFormBuilderStore } from '../../model/form-builder.store';
  import type {
    FormElement,
    HeaderProperties,
    Spacing,
    TableElement as TableElementType,
    TableProperties,
  } from '../../model/form.types';
  import { useFormRulesStore } from '../../model/form-rules.store';
  import { useFormPermissionsStore } from '../../model/form-permissions.store';
  import { useFormPermissionsSelectionStore } from '../../model/form-permissions-selection.store';
  import { useTableEditorStore } from '../../model/table-editor.store';
  import { CanvasElement, ZoomableCanvas } from '../elements';
  import { useCanvasPagination } from '../../composables/useCanvasPagination';
  import { useSheetFit } from '../../composables/useSheetFit';
  import { useCanvasKeyboard } from '../../composables/useCanvasKeyboard';
  import { useCanvasDraggable } from '../../composables/useCanvasDraggable';
  import { useFormBuilderSidebars } from '../../composables/useFormBuilderSidebars';
  import { getElementComponent } from '../../lib/element-component-map';
  import FormHeaderBlock from '../elements/elements/FormHeaderBlock.vue';
  import { CANVAS_LAYOUT } from '../../lib/canvas-constants';
  import { computePageHeight } from '../../lib/sheet-pages';
  import type { TablePageGeometry } from '../../composables/useTablePageBreaks';
  import {
    DEFAULT_FIRST_ELEMENT_AFTER_HEADER_MARGIN_TOP,
    DEFAULT_HEADER_MARGIN,
    DEFAULT_SPACING,
  } from '../../lib/element-defaults';
  import EmptyCanvasState from './EmptyCanvasState.vue';
  import PageBreaks from './PageBreaks.vue';
  import ErrorBoundary from './ErrorBoundary.vue';
  import ConfirmDeleteModal from '@/shared/ui/modals/ConfirmDeleteModal.vue';
  import { isRulesSelectableType } from '../../lib/rules-field-eligibility';
  import { focusUnlessInside } from '../../lib/focus-element';
  import {
    isSelectionInteractionMode,
    type FormBuilderInteractionMode,
  } from '../../lib/interaction-mode';
  import { useRuleAwareElementDeletion } from '../../composables/useRuleAwareElementDeletion';
  import { provideFormBuilderForcedReadonly } from '../../model/useFormBuilderReadonly';
  import {
    normalizeAnchorFieldIds,
    provideFormBuilderAnchorFieldIds,
  } from '../../model/useFormBuilderAnchorFields';

  interface Props {
    orientation: 'portrait' | 'landscape';
    zoom: number;
    interactionMode?: FormBuilderInteractionMode;
    viewScrollTop?: number;
    showAnchorHighlights?: boolean;
    showReportHighlights?: boolean;
    forceReadonly?: boolean;
    /**
     * Anchor field ids handed down by the screen, which also opens anchor marking outside the
     * rules stage. Absent keeps the builder behaviour: the ids come from the rules store.
     */
    anchorFieldIds?: string[];
    lockHeaderRqField?: boolean;
    headerReadonly?: boolean;
    /** Unlike view mode, keeps the builder frame and scale: only the editing goes. */
    locked?: boolean;
  }

  const props = defineProps<Props>();

  provideFormBuilderForcedReadonly(() => props.forceReadonly);
  provideFormBuilderAnchorFieldIds(() => props.anchorFieldIds);

  const emit = defineEmits<{
    'update:zoom': [value: number];
    'update:pageCount': [value: number];
    'update:currentPage': [value: number];
  }>();

  const formBuilderStore = useFormBuilderStore();
  const formRulesStore = useFormRulesStore();
  const formPermissionsStore = useFormPermissionsStore();
  const permissionsSelectionStore = useFormPermissionsSelectionStore();
  const tableEditorStore = useTableEditorStore();
  const { selectedFieldIds: selectedRuleFieldIds } = storeToRefs(formRulesStore);
  const { requestRuleAwareDeletion } = useRuleAwareElementDeletion();

  const outerContainerRef = ref<HTMLElement | null>(null);
  const elementsContainer = ref<HTMLElement | null>(null);
  const canvasPageRef = ref<HTMLElement | null>(null);
  const elementRefs = ref<Map<string, HTMLElement>>(new Map());
  const updateTrigger = ref(0);
  const headerWrapperRef = ref<HTMLElement | null>(null);
  // Reactive header height, kept current by a ResizeObserver. A plain computed over
  // headerWrapperRef.value?.offsetHeight only re-evaluates when the ref identity changes,
  // so it never re-measures after the header's content (title / RQ / REV / logo / fonts)
  // settles — which is exactly what breaks on load/rules paths, where the canvas mounts
  // already-populated and the header expands after its first render.
  const headerHeightRef = ref(0);
  let headerResizeObserver: ResizeObserver | null = null;
  function setHeaderWrapperRef(el: unknown, element: FormElement | undefined, index: number) {
    if (element?.type !== 'HEADER' || index !== 0) return;

    const node = el ? (el as HTMLElement) : null;
    headerWrapperRef.value = node;
    headerResizeObserver?.disconnect();

    if (!node) {
      headerHeightRef.value = 0;
      headerResizeObserver = null;
      return;
    }

    headerHeightRef.value = node.offsetHeight;
    headerResizeObserver = new ResizeObserver(() => {
      headerHeightRef.value = node.offsetHeight;
      updateTrigger.value++;
    });
    headerResizeObserver.observe(node);
  }

  onBeforeUnmount(() => {
    headerResizeObserver?.disconnect();
    headerResizeObserver = null;
  });

  const { sheetWidth: displayWidth, fitScale: viewFitScale } = useSheetFit(
    outerContainerRef,
    () => props.orientation
  );

  const dynamicPageHeight = computed(() =>
    computePageHeight(displayWidth.value, props.orientation)
  );

  const elements = computed({
    get: () => [...formBuilderStore.elements] as FormElement[],
    set: (value: FormElement[]) => {
      formBuilderStore.reorderElements(value);
    },
  });

  const selectedElementId = computed(() => formBuilderStore.selectedElementId);
  const isRulesMode = computed(() => props.interactionMode === 'rules');
  const isPermissionsMode = computed(() => props.interactionMode === 'permissions');
  const isViewMode = computed(() => props.interactionMode === 'view');
  // The builder shows the sheet at real size; a viewer fits the same layout to the screen.
  const fitScale = computed(() => (isViewMode.value ? viewFitScale.value : 1));
  const isSelectionMode = computed(() => isSelectionInteractionMode(props.interactionMode));
  const isInert = computed(() => isViewMode.value || props.locked === true);
  const isCanvasDraggable = computed(() => !isSelectionMode.value && !isInert.value);
  // A locked revision still takes a new RQ, REV. and title, which "Salvar como" and "Enviar para
  // aprovação" carry into a new Registro.
  const isHeaderReadonly = computed(
    () => isSelectionMode.value || isViewMode.value || props.headerReadonly === true
  );
  const canSelectRuleTargets = computed(() => true);
  const focusedRulesElementId = computed(() =>
    formRulesStore.menuAnchor.kind === 'element' ? formRulesStore.menuAnchor.elementId : null
  );
  const hasElements = computed(() => formBuilderStore.hasElements);

  const providedAnchorFieldIds = computed(() =>
    props.anchorFieldIds === undefined ? null : normalizeAnchorFieldIds(props.anchorFieldIds)
  );
  const canMarkAnchorFields = computed(
    () => isRulesMode.value || providedAnchorFieldIds.value !== null
  );

  function isFieldAnchorConfigured(fieldId: string): boolean {
    const providedIds = providedAnchorFieldIds.value;
    return providedIds
      ? providedIds.includes(fieldId)
      : formRulesStore.isFieldAnchorConfigured(fieldId);
  }

  const orientationRef = computed(() => props.orientation);

  const canvasPagination = useCanvasPagination(
    elements,
    elementRefs,
    hasElements,
    orientationRef,
    updateTrigger,
    dynamicPageHeight,
    headerHeightRef
  );
  const { pageCount, canvasHeight, pageBreakPositions, getElementMarginTop } = canvasPagination;

  const canvasHeightPx = computed(() => {
    const raw = canvasHeight.value;
    return typeof raw === 'number' ? raw : Number.parseFloat(raw);
  });

  const headerElement = computed(() => {
    const first = elements.value[0];
    return first?.type === 'HEADER' ? first : null;
  });

  const getElementById = (elementId: string) => {
    return formBuilderStore.elements.find((el) => el.id === elementId);
  };

  const isHeaderTable = (elementId: string): boolean => {
    const element = getElementById(elementId);
    if (element?.type === 'TABLE') {
      return (element.properties as TableProperties).isHeader === true;
    }
    return false;
  };

  const requiresDeleteConfirmation = (elementId: string): boolean => {
    const element = getElementById(elementId);
    return element?.type === 'TABLE';
  };

  const getDeleteModalContent = (elementId: string) => {
    if (isHeaderTable(elementId)) {
      return {
        title: 'Tem certeza que deseja deletar o cabeçalho?',
        description: 'A ação não pode ser desfeita e as edições feitas serão perdidas.',
      };
    }
    return {
      title: 'Tem certeza que deseja deletar a tabela?',
      description: 'A ação não pode ser desfeita e as edições feitas serão perdidas.',
    };
  };

  const deleteElement = (elementId: string, withUndo = true) => {
    if (removeFormBuilderBodyTable(elementId)) return;
    removeFormBuilderCanvasElement(elementId, withUndo);
  };

  const openDeleteConfirmationModal = (elementId: string) => {
    const { title, description } = getDeleteModalContent(elementId);

    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        title,
        description,
        onClose() {
          close();
        },
        onConfirm() {
          close();
          deleteElement(elementId);
        },
      },
    });

    open();
  };

  const isHeaderElement = (elementId: string): boolean => {
    const element = getElementById(elementId);
    if (element?.type === 'HEADER') return true;
    if (element?.type === 'TABLE' && (element.properties as TableProperties).isHeader === true)
      return true;
    return false;
  };

  const handleRequestDelete = (elementId: string) => {
    if (isHeaderElement(elementId)) return;
    const element = getElementById(elementId);
    if (!element) return;
    if (requestRuleAwareDeletion(element, () => deleteElement(elementId, false))) {
      return;
    }
    if (requiresDeleteConfirmation(elementId)) {
      openDeleteConfirmationModal(elementId);
    } else {
      deleteElement(elementId);
    }
  };

  const handleRequestDeleteCellElement = (element: FormElement) => {
    if (
      requestRuleAwareDeletion(element, () => {
        removeFormBuilderTableCellElement(false);
      })
    ) {
      return;
    }

    removeFormBuilderTableCellElement();
  };

  useCanvasKeyboard(selectedElementId, {
    onRequestDelete: (elementId: string) => {
      if (isInert.value) return;
      handleRequestDelete(elementId);
    },
    onRequestDeleteCellElement: (element: FormElement) => {
      if (isInert.value) return;
      handleRequestDeleteCellElement(element);
    },
    shouldHandleHistoryShortcut: () => !isSelectionMode.value && !isInert.value,
  });

  const { evaluateAutoCollapse } = useFormBuilderSidebars();

  useCanvasDraggable(elementsContainer, elements, elementRefs, hasElements, isCanvasDraggable);

  provide('elementRefs', elementRefs);

  // Distance from the table wrapper's content-box top (accumulatedTop) down to the
  // first grid row: the .table-grid-container has 8px of top padding. Used only as a
  // fallback when the grid is not measurable yet.
  const TABLE_GRID_TOP_INSET = 8;

  // Real top of the table's grid within the .canvas-page coordinate space (the same
  // space page boundaries live in). offsetTop is layout px, unaffected by the zoom
  // transform, so this stays correct across zoom levels and header margins.
  const measureGridTopInCanvas = (gridEl: HTMLElement): number | null => {
    const canvasPage = canvasPageRef.value;
    if (!canvasPage) return null;

    let top = 0;
    let node: HTMLElement | null = gridEl;
    while (node && node !== canvasPage) {
      top += node.offsetTop;
      node = node.offsetParent as HTMLElement | null;
    }
    return node === canvasPage ? top : null;
  };

  const getTablePageGeometry = (element: TableElementType): TablePageGeometry | null => {
    const position = canvasPagination.elementPositions.value.get(element.id);

    const elementRef = elementRefs.value.get(element.id);
    const gridEl = elementRef?.querySelector('.table-grid') as HTMLElement | null;
    const measuredTop = gridEl ? measureGridTopInCanvas(gridEl) : null;

    let firstRowTop: number;
    if (measuredTop !== null) {
      firstRowTop = measuredTop;
    } else if (position) {
      const marginTop = element.properties.margin?.top ?? 0;
      firstRowTop = position.accumulatedTop + marginTop + TABLE_GRID_TOP_INSET;
    } else {
      return null;
    }

    const headerHeight = headerHeightRef.value;
    const contentStartOffset =
      headerHeight > 0
        ? CANVAS_LAYOUT.CANVAS_PADDING +
          headerHeight +
          DEFAULT_FIRST_ELEMENT_AFTER_HEADER_MARGIN_TOP
        : CANVAS_LAYOUT.TOP_MARGIN;

    return {
      firstRowTop,
      pageHeight: dynamicPageHeight.value,
      contentStartOffset,
      bottomMargin: CANVAS_LAYOUT.BOTTOM_MARGIN,
    };
  };

  const requestPaginationUpdate = () => {
    updateTrigger.value++;
  };

  provide('getTablePageGeometry', getTablePageGeometry);
  provide('requestPaginationUpdate', requestPaginationUpdate);
  provide('paginationTick', updateTrigger);

  function handleZoomUpdate(newZoom: number) {
    emit('update:zoom', newZoom);
    if (outerContainerRef.value) {
      evaluateAutoCollapse(
        displayWidth.value * newZoom * fitScale.value,
        outerContainerRef.value.clientWidth
      );
    }
  }

  watch(
    () => elements.value.length,
    async (newLen, oldLen) => {
      if (newLen !== oldLen) {
        await nextTick();
        updateTrigger.value++;
        setTimeout(() => updateTrigger.value++, 300);
      }
    }
  );

  watch(pageCount, (n) => emit('update:pageCount', n), { immediate: true });

  const scrollTop = ref(0);

  const effectiveScrollTop = computed(() =>
    isViewMode.value ? (props.viewScrollTop ?? 0) : scrollTop.value
  );

  const currentPage = computed(() =>
    Math.max(
      1,
      Math.min(
        Math.floor(
          effectiveScrollTop.value / (dynamicPageHeight.value * props.zoom * fitScale.value)
        ) + 1,
        pageCount.value
      )
    )
  );

  watch(currentPage, (n) => emit('update:currentPage', n), { immediate: true });

  function handleScrollTop(value: number) {
    scrollTop.value = value;
  }

  function syncAutomaticHeaderSpacing() {
    const pageHeight = dynamicPageHeight.value;
    const currentElements = elements.value;

    currentElements.forEach((element, index) => {
      if (!element || element.type === 'HEADER' || element.type === 'TABLE') {
        return;
      }

      const positions = canvasPagination.elementPositions?.value;
      if (!positions) {
        return;
      }

      const currentPosition = positions.get(element.id);
      if (!currentPosition) {
        return;
      }

      const previousElement = index > 0 ? currentElements[index - 1] : null;
      const previousPosition = previousElement ? positions.get(previousElement.id) : null;

      const currentPage = Math.floor(currentPosition.accumulatedTop / pageHeight);
      const previousPage = previousPosition
        ? Math.floor(previousPosition.accumulatedTop / pageHeight)
        : currentPage;

      const shouldUseAutoHeaderSpacing =
        previousElement?.type === 'HEADER' || currentPage > previousPage;

      const currentMargin = element.properties.margin ?? DEFAULT_SPACING.margin;
      const currentTopMargin = currentMargin.top ?? DEFAULT_SPACING.margin.top;
      const hasAutoHeaderSpacing = element.properties.autoHeaderSpacing === true;

      if (shouldUseAutoHeaderSpacing) {
        if (
          hasAutoHeaderSpacing &&
          currentTopMargin === DEFAULT_FIRST_ELEMENT_AFTER_HEADER_MARGIN_TOP
        ) {
          return;
        }

        if (!hasAutoHeaderSpacing && currentTopMargin !== DEFAULT_SPACING.margin.top) {
          return;
        }

        formBuilderStore.updateElement(
          element.id,
          {
            properties: {
              ...element.properties,
              autoHeaderSpacing: true,
              margin: {
                top: DEFAULT_FIRST_ELEMENT_AFTER_HEADER_MARGIN_TOP,
                right: currentMargin.right ?? DEFAULT_SPACING.margin.right,
                bottom: currentMargin.bottom ?? DEFAULT_SPACING.margin.bottom,
                left: currentMargin.left ?? DEFAULT_SPACING.margin.left,
              },
            },
          },
          { trackHistory: false }
        );
        return;
      }

      if (!hasAutoHeaderSpacing) {
        return;
      }

      formBuilderStore.updateElement(
        element.id,
        {
          properties: {
            ...element.properties,
            autoHeaderSpacing: false,
            margin: {
              top: DEFAULT_SPACING.margin.top,
              right: currentMargin.right ?? DEFAULT_SPACING.margin.right,
              bottom: currentMargin.bottom ?? DEFAULT_SPACING.margin.bottom,
              left: currentMargin.left ?? DEFAULT_SPACING.margin.left,
            },
          },
        },
        { trackHistory: false }
      );
    });
  }

  watch(
    [
      () => updateTrigger.value,
      () => pageCount.value,
      () => dynamicPageHeight.value,
      () =>
        elements.value
          .map((element) => {
            const marginTop = element?.properties.margin?.top ?? DEFAULT_SPACING.margin.top;
            const autoHeaderSpacing = element?.properties.autoHeaderSpacing === true ? '1' : '0';
            return `${element?.id}:${marginTop}:${autoHeaderSpacing}`;
          })
          .join('|'),
    ],
    async () => {
      await nextTick();
      syncAutomaticHeaderSpacing();
    },
    { flush: 'post' }
  );

  watch(
    () => (isRulesMode.value ? focusedRulesElementId.value : selectedElementId.value),
    async (newId) => {
      if (newId) {
        await nextTick();
        setTimeout(() => {
          focusUnlessInside(elementRefs.value.get(newId));
        }, 50);
      }
    }
  );

  const registerElementRef = (elementId: string, el: Element | ComponentPublicInstance | null) => {
    if (el && '$el' in el && el.$el) {
      elementRefs.value.set(elementId, el.$el as HTMLElement);
    } else if (el) {
      elementRefs.value.set(elementId, el as HTMLElement);
    }
  };

  const selectPermissionsTarget = (elementId: string, event?: MouseEvent | KeyboardEvent) => {
    const element = getElementById(elementId);
    if (!element || !isRulesSelectableType(element.type)) return;

    if (!permissionsSelectionStore.addMode) {
      permissionsSelectionStore.dismissFill();
      permissionsSelectionStore.pickField(elementId);
      return;
    }

    permissionsSelectionStore.selectField(elements.value, elementId, {
      additive: !!event && (event.metaKey || event.ctrlKey),
      range: !!event && event.shiftKey,
    });
  };

  const handleSelect = (
    elementId: string,
    event?: MouseEvent | KeyboardEvent,
    headerPageIndex?: number
  ) => {
    if (isInert.value) return;

    if (isPermissionsMode.value) {
      selectPermissionsTarget(elementId, event);
      return;
    }

    if (isRulesMode.value) {
      if (!canSelectRuleTargets.value) return;

      const element = getElementById(elementId);
      if (!element || !isRulesSelectableType(element.type)) {
        return;
      }

      const additive = !!event && (event.metaKey || event.ctrlKey || event.shiftKey);
      formRulesStore.selectStandaloneField(element, additive);
      const triggerRuleId = formRulesStore.getTriggerRuleId(element);
      if (!formRulesStore.ruleItemSelectionActive && triggerRuleId) {
        formRulesStore.focusRule(triggerRuleId);
      }
      return;
    }

    formBuilderStore.selectElement(elementId, headerPageIndex);
  };

  const handleDelete = (elementId: string) => {
    handleRequestDelete(elementId);
  };

  const getElementWrapperStyle = (element: FormElement | undefined) => {
    if (!element) {
      return { marginTop: '0px' };
    }

    const wrapperStyle: Record<string, string> = {
      marginTop: getElementMarginTop(element.id),
    };

    let margin: Spacing | undefined;
    if (element.type === 'HEADER') {
      const headerProps = element.properties as HeaderProperties;
      margin = headerProps.marginByPage?.[0] ?? headerProps.margin ?? DEFAULT_HEADER_MARGIN;
    } else {
      margin = element.properties.margin;
    }

    if (!margin) {
      return wrapperStyle;
    }

    if (margin.top !== undefined) wrapperStyle.paddingTop = `${margin.top}px`;
    if (margin.right !== undefined) wrapperStyle.paddingRight = `${margin.right}px`;
    if (margin.bottom !== undefined) wrapperStyle.paddingBottom = `${margin.bottom}px`;
    if (margin.left !== undefined) wrapperStyle.paddingLeft = `${margin.left}px`;

    return wrapperStyle;
  };

  const isElementSelected = (element: FormElement) => {
    if (isPermissionsMode.value) {
      return (
        element.type !== 'TABLE' && permissionsSelectionStore.isFieldSelected(element.id)
      );
    }

    if (!isRulesMode.value) {
      return selectedElementId.value === element.id;
    }

    if (formRulesStore.ruleItemSelectionActive) {
      return formRulesStore.isDraftTriggerField(element.id);
    }

    if (element.type === 'TABLE') {
      return formRulesStore.activeTableId === element.id;
    }

    return selectedRuleFieldIds.value.includes(element.id);
  };

  const shouldShowRulesMenu = (element: FormElement) => {
    if (!isRulesMode.value || !isRulesSelectableType(element.type)) {
      return false;
    }

    if (formRulesStore.ruleItemSelectionActive) {
      return false;
    }

    return (
      selectedRuleFieldIds.value.includes(element.id) &&
      formRulesStore.isElementAnchor(element.id) &&
      formRulesStore.hasSelection
    );
  };

  const getPermissionOutline = (element: FormElement) => {
    if (!isPermissionsMode.value || element.type === 'TABLE') return null;
    return permissionsSelectionStore.getOutline(
      element.id,
      formPermissionsStore.isFieldPermissioned(element.id)
    );
  };

  const handleCanvasClick = (e: MouseEvent) => {
    if (isInert.value) return;
    const target = e.target as HTMLElement;
    if (isPermissionsMode.value) {
      if (!permissionsSelectionStore.addMode) {
        permissionsSelectionStore.dismissFill();
        if (!target.closest('.element-wrapper')) permissionsSelectionStore.clearPick();
      }
      return;
    }

    if (!target.closest('.element-wrapper')) {

      if (isRulesMode.value) {
        if (formRulesStore.ruleItemSelectionActive) return;
        formRulesStore.clearSelection();
        return;
      }

      formBuilderStore.selectElement(null);
      tableEditorStore.exitEditingMode();
    }
  };
</script>

<template>
  <div
    ref="outerContainerRef"
    class="flex flex-col min-w-0"
    :class="isViewMode ? 'bg-transparent' : 'flex-1 overflow-hidden bg-gray-100'"
    @click="handleCanvasClick"
  >
    <ZoomableCanvas
      :zoom="props.zoom"
      :fit-scale="fitScale"
      :display-width="displayWidth"
      :page-scroll="isViewMode"
      :content-height="canvasHeightPx"
      @update:zoom="handleZoomUpdate"
      @update:scrollTop="handleScrollTop"
    >
      <div
        ref="canvasPageRef"
        class="canvas-page canvas-background light-surface bg-white-01 relative [--font-base:Arial,Helvetica,sans-serif] font-[family-name:var(--font-base)]"
        :class="{ 'rounded-md shadow-md': isViewMode }"
        :style="{
          width: `${displayWidth}px`,
          height: canvasHeight,
          ...(isViewMode
            ? {}
            : {
                boxShadow:
                  '0 0 0 1px rgba(0, 0, 0, 0.08), 0 10px 12px -6px rgba(0, 0, 0, 0.12), 0 4px 6px -4px rgba(0, 0, 0, 0.08)',
              }),
        }"
      >
        <PageBreaks :page-breaks="pageBreakPositions" :total-pages="pageCount" />

        <template v-if="headerElement && pageBreakPositions.length > 0">
          <div
            v-for="(pageBreak, idx) in pageBreakPositions"
            :key="`header-clone-${pageBreak.pageNumber}`"
            class="absolute left-0 right-0"
            :style="{ top: `${pageBreak.top}px` }"
          >
            <div class="p-8">
              <FormHeaderBlock
                :element="headerElement"
                :page-index="pageBreak.pageNumber - 1"
                :on-select="(pi) => headerElement && handleSelect(headerElement.id, undefined, pi)"
                :readonly="isHeaderReadonly"
                :lock-rq-field="props.lockHeaderRqField"
              />
            </div>
          </div>
        </template>

        <div class="p-8">
          <EmptyCanvasState v-if="!hasElements" />

          <div v-if="hasElements" ref="elementsContainer">
            <div
              v-for="(element, index) in elements"
              :key="element?.id || index"
              :ref="(el) => setHeaderWrapperRef(el, element, index)"
              class="element-wrapper"
              :style="getElementWrapperStyle(element)"
            >
              <CanvasElement
                v-if="element"
                :element="element"
                :is-selected="isElementSelected(element)"
                :ref="
                  (el: Element | ComponentPublicInstance | null) =>
                    registerElementRef(element.id, el)
                "
                :interaction-mode="props.interactionMode ?? 'builder'"
                :permission-outline="getPermissionOutline(element)"
                :show-selection-fill="permissionsSelectionStore.fillVisible"
                :show-rules-menu="shouldShowRulesMenu(element)"
                :show-add-rule="
                  shouldShowRulesMenu(element) &&
                  formRulesStore.canAddRule &&
                  formRulesStore.canAddRuleForElement(element)
                "
                :has-applied-rule="
                  isRulesMode && formRulesStore.hasAppliedTriggerRule(element.id)
                "
                :is-anchor-configured="
                  canMarkAnchorFields &&
                  props.showAnchorHighlights &&
                  isFieldAnchorConfigured(element.id)
                "
                :is-report-configured="
                  isRulesMode &&
                  props.showReportHighlights &&
                  formRulesStore.isFieldReportConfigured(element.id)
                "
                @select="handleSelect"
                @close-rules-menu="formRulesStore.clearSelection"
              >
                <ErrorBoundary
                  v-if="element.type === 'TABLE'"
                  fallback-message="Erro ao renderizar tabela"
                >
                  <component
                    :is="getElementComponent(element.type)"
                    :element="element as TableElementType"
                    :interaction-mode="props.interactionMode ?? 'builder'"
                    :show-anchor-highlights="props.showAnchorHighlights"
                    :show-report-highlights="props.showReportHighlights"
                    :locked="props.locked"
                  />
                </ErrorBoundary>

                <component
                  v-else-if="getElementComponent(element.type)"
                  :is="getElementComponent(element.type)"
                  :element="element"
                  :page-index="element.type === 'HEADER' ? 0 : undefined"
                  :on-select="
                    element.type === 'HEADER'
                      ? (pi: number) => handleSelect(element.id, undefined, pi)
                      : undefined
                  "
                  :apply-margin="element.type === 'HEADER' ? false : undefined"
                  :readonly="element.type === 'HEADER' ? isHeaderReadonly : undefined"
                  :lock-rq-field="element.type === 'HEADER' ? lockHeaderRqField : undefined"
                />

                <div v-else class="text-sm text-gray-500">
                  {{ element.type }} - Tipo desconhecido
                </div>
              </CanvasElement>
            </div>
          </div>
        </div>
      </div>
    </ZoomableCanvas>
  </div>
</template>
